import { guitarSetups, isLegacyDoubleDropD, selectNpsValues } from "./chartOptions.js";

const object = value => value !== null && typeof value === "object" && !Array.isArray(value);
const number = (value, min, max) => Number.isFinite(value) && value >= min && value <= max;
const integer = (value, min, max) => Number.isSafeInteger(value) && number(value, min, max);
const bools = value => Array.isArray(value) && value.length === 12 && value.every(v => typeof v === "boolean");
const pick = (source, keys) => Object.fromEntries(keys.map(key => [key, source[key]]));
const defaultKeys = ["intervalColors", "guitarSetup", "tuning", "midiRootNote", "imageScale", "selectNps"];
const chartKeys = [...defaultKeys, "title", "introduction", "intervals", "refineData"];
const earKeys = ["earTrainerQuestionType", "earTrainerShowNotes", "earTrainerShapeChange",
  "earTrainerRootNote", "earTrainerRootOctaveSelect", "earTrainerIgnoreOctaves"];
const fretboardKeys = ["fretboardNotesDisplay", "fretboardUseSharps", "fretboardShowNoteNames"];
const metronomeKeys = ["metronomeStartingBpm", "metronomeRamping", "metronomeVolume"];

function validDefaults(value) {
  return object(value) && Array.isArray(value.intervalColors) && value.intervalColors.length === 12 &&
    value.intervalColors.every(color => typeof color === "string" && color.length >= 1 && color.length <= 128) &&
    typeof value.guitarSetup === "string" && Object.hasOwn(guitarSetups, value.guitarSetup) && Array.isArray(value.tuning) &&
    value.tuning.length >= 1 && value.tuning.length <= 20 && value.tuning.every(v => integer(v, 1, 127)) &&
    integer(value.midiRootNote, 0, 127) &&
    (value.guitarSetup === "custom" || ((JSON.stringify(value.tuning) === JSON.stringify(guitarSetups[value.guitarSetup][1]) ||
      isLegacyDoubleDropD(value.guitarSetup, value.tuning)) &&
      value.midiRootNote === guitarSetups[value.guitarSetup][0])) &&
    number(value.imageScale, Number.MIN_VALUE, 1000) && selectNpsValues.includes(value.selectNps);
}

function validChart(chart) {
  return validDefaults(chart) && typeof chart.title === "string" && chart.title.length <= 1000 &&
    typeof chart.introduction === "string" && chart.introduction.length <= 100000 &&
    bools(chart.intervals) && object(chart.refineData) && Array.isArray(chart.refineData.perChartData) && chart.refineData.perChartData.length <= 512 &&
    chart.refineData.perChartData.every(row => {
      if (!object(row) || !Array.isArray(row.notesPerString) || row.notesPerString.length !== chart.tuning.length + 1 ||
          !row.notesPerString.every(v => integer(v, 0, 128)) || !integer(row.notesOffset, -10000, 10000)) return false;
      const total = row.notesPerString.reduce((sum, count) => sum + count, 0);
      return Number.isSafeInteger(total) && (!Object.hasOwn(row, "omittedNotes") ||
        (Array.isArray(row.omittedNotes) && row.omittedNotes.every(id => integer(id, 0, total - 1)) &&
          new Set(row.omittedNotes).size === row.omittedNotes.length));
    });
}

function defaultsCopy(source) {
  const result = pick(source, defaultKeys);
  if (isLegacyDoubleDropD(result.guitarSetup, result.tuning)) result.guitarSetup = "custom";
  return result;
}

// Explicit field selection keeps histories, resources and future provider metadata out.
function chartCopy(chart) {
  const result = { ...pick(chart, chartKeys), ...defaultsCopy(chart) };
  result.refineData = { perChartData: chart.refineData.perChartData.map(row => ({
    notesPerString: [...row.notesPerString], notesOffset: row.notesOffset,
    ...(Object.hasOwn(row, "omittedNotes") ? { omittedNotes: [...row.omittedNotes] } : {}),
  })) };
  return JSON.parse(JSON.stringify(result));
}

export function readChartDocument(document) {
  if (!object(document) || document.schemaVersion !== 1 || !validChart(document.chart)) return null;
  return { schemaVersion: 1, chart: chartCopy(document.chart) };
}

export function exportChartDocument(state) {
  const document = readChartDocument({ schemaVersion: 1, chart: state });
  if (!document) throw new Error("The chart cannot be saved because its data is invalid.");
  return document;
}

export function readPreferencesDocument(document) {
  if (!object(document) || document.schemaVersion !== 1 || !validDefaults(document.chartDefaults)) return null;
  const { earTrainer: ear, fretboard: fret, metronome: metro } = document;
  if (!object(ear) || !object(fret) || !object(metro) ||
      !integer(ear.earTrainerQuestionType, 0, 2) || !integer(ear.earTrainerShowNotes, 0, 3) ||
      ![0, 1, 2, 4, 8, 16, 32].includes(ear.earTrainerShapeChange) || !integer(ear.earTrainerRootNote, 0, 127) ||
      !integer(ear.earTrainerRootOctaveSelect, 0, 1) || !integer(ear.earTrainerIgnoreOctaves, 0, 1) ||
      !bools(fret.fretboardNotesDisplay) || !integer(fret.fretboardUseSharps, 0, 2) ||
      typeof fret.fretboardShowNoteNames !== "boolean" ||
      !number(metro.metronomeStartingBpm, 10, 400) || typeof metro.metronomeRamping !== "boolean" ||
      !number(metro.metronomeVolume, 0, 100)) return null;
  return JSON.parse(JSON.stringify({ schemaVersion: 1, chartDefaults: defaultsCopy(document.chartDefaults),
    earTrainer: pick(ear, earKeys), fretboard: pick(fret, fretboardKeys), metronome: pick(metro, metronomeKeys) }));
}

// Export is explicit: capture current choices only when the user asks to save defaults.
export function exportPreferencesDocument(state) {
  const document = readPreferencesDocument({ schemaVersion: 1,
    chartDefaults: pick(state, defaultKeys), earTrainer: pick(state, earKeys),
    fretboard: pick(state, fretboardKeys), metronome: pick(state, metronomeKeys) });
  if (!document) throw new Error("Preferences cannot be saved because their data is invalid.");
  return document;
}

export function exportSyncedPreferencesDocument(state, defaultChartDefaults) {
  const document = readPreferencesDocument({
    schemaVersion: 1,
    chartDefaults: state.accountChartDefaults ?? defaultChartDefaults,
    earTrainer: pick(state, earKeys),
    fretboard: pick(state, fretboardKeys),
    metronome: pick(state, metronomeKeys),
  });
  if (!document) throw new Error("Preferences cannot be synced because their data is invalid.");
  return document;
}

export function parsePersonalDataJson(json, readDocument) {
  if (typeof json !== "string") return null;
  try { return readDocument(JSON.parse(json)); } catch { return null; }
}
